import React, { useState } from 'react';
import { useHistory } from "react-router-dom";
import { t } from "../../common/LocalizationProvider"; // Assuming this path is correct

import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

const useStyles = makeStyles((theme) => ({
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.grey[100], // Light grey background
        padding: theme.spacing(2), // Add some padding for smaller screens
    },
    formCard: {
        padding: theme.spacing(4), // More padding inside the card
        width: '100%',
        maxWidth: 420, // Slightly wider for better feel
        textAlign: 'center',
        boxShadow: theme.shadows[3], // Softer shadow
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 600,
    },
    description: {
        marginBottom: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    form: {
        width: '100%', // Ensure form takes full width of Paper
        marginTop: theme.spacing(1),
    },
    textField: {
        marginBottom: theme.spacing(2.5), // Spacing below the text field
    },
    submitButton: {
        margin: theme.spacing(3, 0, 2), // top, horizontal, bottom
        padding: theme.spacing(1.5), // Make button a bit taller
        fontWeight: 'bold',
    },
    errorMessage: {
        marginTop: theme.spacing(2),
        minHeight: theme.spacing(2.5), // Reserve space
        color: theme.palette.error.main,
    },
    spinner: {
        marginLeft: theme.spacing(1),
    }
}));

const ForgotPassword = () => {
    const classes = useStyles(); // Hook to get styles
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setMessage('');

        try {
            const response = await fetch('/api/session/senemail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ email })
            });

            if (response.ok) {
                history.push('/checkEmailMessage');
            } else {
                const errorText = await response.text();
                const errorKey = errorText.split(' ')[0] || "errorMessage";
                setMessage(t(errorKey));
            }
        } catch (error) {
            setMessage(t("networkError"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth={false} className={classes.pageContainer}>
            <Paper className={classes.formCard} elevation={3}>
                <Typography component="h1" variant="h5" className={classes.title}>
                    {t("findAccount")}
                </Typography>
                <Typography component="p" className={classes.description}>
                    {t("searchForAccount")}
                </Typography>
                <form className={classes.form} onSubmit={handleSubmit} noValidate>
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label={t("emailAddress")}
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        className={classes.textField}
                        disabled={isLoading}
                        placeholder={"e.g., user@example.com"}
                        InputLabelProps={{
                            shrink: true, // Ensures label doesn't overlap with placeholder if placeholder is also used
                        }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className={classes.submitButton}
                        disabled={isLoading}
                    >
                        {isLoading ? t("searching") : t("sharedSearch")}
                        {isLoading && (
                            <CircularProgress
                                size={20}
                                color="inherit" // Inherits button's text color (white for primary)
                                className={classes.spinner}
                            />
                        )}
                    </Button>
                </form>
                {message && (
                    <Typography
                        className={classes.errorMessage}
                        color="error" // Uses theme's error color
                        aria-live="polite"
                    >
                        {message}
                    </Typography>
                )}
            </Paper>
        </Container>
    );
}

export default ForgotPassword;